import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { EntityImage } from '../components/EntityImage';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Heart, Trash2, Edit, DollarSign, ExternalLink, Upload, X, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { MAX_IMAGES_PER_ITEM, canAddMoreImages, getImageCountDisplay, validateImageUpload } from '../lib/imageLimit';

const WISHLIST_CATEGORIES = ['Firearm', 'Ammo', 'Accessory'];
const PRIORITIES = ['Low', 'Medium', 'High'];

export default function WishlistFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const isDetail = isEdit && !window.location.pathname.includes('/edit');

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [item, setItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    target_price: '',
    priority: 'Medium',
    notes: '',
    url: '',
    images: [],
  });
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    if (isEdit) {
      const fetchItem = async () => {
        try {
          const response = await api.get(`/wishlist/${id}`);
          setItem(response.data);
          setFormData({
            name: response.data.name || '',
            category: response.data.category || '',
            target_price: response.data.target_price?.toString() || '',
            priority: response.data.priority || 'Medium',
            notes: response.data.notes || '',
            url: response.data.url || '',
            images: response.data.images || [],
          });
        } catch (error) {
          toast.error('Item not found');
          navigate('/wishlist');
        } finally {
          setLoading(false);
        }
      };
      fetchItem();
    }
  }, [id, isEdit, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const { acceptedFiles, message } = validateImageUpload(files, formData.images.length);
    
    if (message) {
      toast.error(message);
    }
    
    if (acceptedFiles.length === 0) {
      e.target.value = '';
      return;
    }
    
    acceptedFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, reader.result],
        }));
      };
      reader.readAsDataURL(file);
    });
    
    e.target.value = '';
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
    if (selectedImage >= index && selectedImage > 0) {
      setSelectedImage(selectedImage - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.category) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        target_price: formData.target_price ? parseFloat(formData.target_price) : null,
      };

      if (isEdit) {
        await api.put(`/wishlist/${id}`, payload);
        toast.success('Item updated');
      } else {
        await api.post('/wishlist', payload);
        toast.success('Item added to wishlist');
      }
      navigate('/wishlist');
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error('Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/wishlist/${id}`);
      toast.success('Item removed from wishlist');
      navigate('/wishlist');
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete item');
    } finally {
      setDeleting(false);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'High': 'bg-destructive/20 text-destructive border-destructive/30',
      'Medium': 'bg-accent/20 text-accent border-accent/30',
      'Low': 'bg-muted text-muted-foreground border-muted',
    };
    return colors[priority] || colors['Medium'];
  };

  if (loading) {
    return (
      <Layout title={isEdit ? 'Wishlist Item' : 'Add to Wishlist'}>
        <div className="animate-pulse space-y-6 max-w-2xl">
          <div className="h-10 bg-muted rounded w-full" />
          <div className="h-10 bg-muted rounded w-full" />
        </div>
      </Layout>
    );
  }

  // Detail view
  if (isDetail && item) {
    return (
      <Layout title="Wishlist Item">
        <div className="max-w-3xl" data-testid="wishlist-detail-page">
          <Button
            variant="ghost"
            onClick={() => navigate('/wishlist')}
            className="mb-6"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Wishlist
          </Button>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Image Gallery */}
            <div className="space-y-4">
              <div className="aspect-square bg-card border border-border rounded-sm overflow-hidden">
                {item.images?.length > 0 ? (
                  <EntityImage
                    fileId={item.images[selectedImage]}
                    alt={item.name}
                    className="w-full h-full object-contain bg-muted"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-muted">
                    <Heart className="w-24 h-24 text-muted-foreground/30" />
                  </div>
                )}
              </div>

              {/* Thumbnails */}
              {item.images?.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {item.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`w-16 h-16 flex-shrink-0 rounded-sm overflow-hidden border-2 transition-colors ${
                        selectedImage === idx ? 'border-secondary' : 'border-border hover:border-muted-foreground'
                      }`}
                    >
                      <EntityImage fileId={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div className="space-y-6">
              {/* Header */}
              <div className="bg-card border border-border rounded-sm p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <Badge variant="outline" className={`rounded-sm ${getPriorityColor(item.priority)}`}>
                        {item.priority} Priority
                      </Badge>
                      <span className="text-sm text-muted-foreground">{item.category}</span>
                    </div>
                    <h1 className="text-3xl font-bold text-foreground font-['Chivo'] tracking-tight">
                      {item.name}
                    </h1>
                    {item.target_price && (
                      <p className="text-xl text-muted-foreground flex items-center gap-1 mt-2">
                        <DollarSign className="w-5 h-5" />
                        {item.target_price.toLocaleString()}
                        <span className="text-sm ml-1">target price</span>
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/wishlist/${id}/edit`)}
                      className="rounded-sm"
                      data-testid="edit-btn"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-sm text-destructive hover:bg-destructive hover:text-destructive-foreground"
                          data-testid="delete-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove from Wishlist</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to remove this item from your wishlist?
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            disabled={deleting}
                          >
                            {deleting ? 'Removing...' : 'Remove'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>

              {/* URL */}
              {item.url && (
                <div className="bg-card border border-border rounded-sm p-6">
                  <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
                    Link
                  </h3>
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#C9A24D] hover:text-[#D4B05E] hover:underline break-all"
                  >
                    <ExternalLink className="w-4 h-4 flex-shrink-0" />
                    {item.url}
                  </a>
                </div>
              )}

              {/* Notes */}
              {item.notes && (
                <div className="bg-card border border-border rounded-sm p-6">
                  <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
                    Notes
                  </h3>
                  <p className="text-foreground whitespace-pre-wrap">{item.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Form view
  return (
    <Layout title={isEdit ? 'Edit Wishlist Item' : 'Add to Wishlist'}>
      <div className="max-w-2xl" data-testid="wishlist-form-page">
        <Button
          variant="ghost"
          onClick={() => navigate('/wishlist')}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Wishlist
        </Button>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-card border border-border rounded-sm p-6 space-y-4">
            <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
              Item Details
            </h3>

            <div className="space-y-2">
              <Label htmlFor="name">Item Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g., Glock 43X, 9mm Ammo, Red Dot Sight"
                className="rounded-sm"
                required
                data-testid="input-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select value={formData.category} onValueChange={(v) => handleSelectChange('category', v)}>
                  <SelectTrigger className="rounded-sm" data-testid="select-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {WISHLIST_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => handleSelectChange('priority', v)}>
                  <SelectTrigger className="rounded-sm" data-testid="select-priority">
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="target_price">Target Price ($)</Label>
              <Input
                id="target_price"
                name="target_price"
                type="number"
                step="0.01"
                value={formData.target_price}
                onChange={handleInputChange}
                placeholder="e.g., 499.99"
                className="rounded-sm"
                data-testid="input-price"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="url">Link (URL)</Label>
              <Input
                id="url"
                name="url"
                type="url"
                value={formData.url}
                onChange={handleInputChange}
                placeholder="https://..."
                className="rounded-sm"
                data-testid="input-url"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Add any notes..."
                rows={3}
                className="rounded-sm"
                data-testid="input-notes"
              />
            </div>
          </div>

          {/* Images */}
          <div className="bg-card border border-border rounded-sm p-6 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo']">
                Images
              </h3>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <ImageIcon className="w-3 h-3" />
                {getImageCountDisplay(formData.images.length)}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Upload images of the item you want (product photos, screenshots, etc.)
            </p>
            <div className="grid grid-cols-4 gap-4">
              {formData.images.map((img, idx) => (
                <div key={idx} className="relative aspect-square bg-muted rounded-sm overflow-hidden group">
                  <EntityImage fileId={img} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImage(idx)}
                    className="absolute top-2 right-2 p-1 bg-destructive text-destructive-foreground rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {canAddMoreImages(formData.images.length) && (
                <label className="aspect-square border-2 border-dashed border-border rounded-sm flex flex-col items-center justify-center cursor-pointer hover:border-secondary transition-colors">
                  <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                  <span className="text-xs text-muted-foreground">Upload</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="input-images"
                  />
                </label>
              )}
            </div>
            {formData.images.length >= MAX_IMAGES_PER_ITEM && (
              <p className="text-xs text-muted-foreground">
                Maximum {MAX_IMAGES_PER_ITEM} photos reached. Remove existing photos to add new ones.
              </p>
            )}
          </div>

          <div className="flex gap-4">
            <Button
              type="submit"
              disabled={saving}
              className="bg-destructive hover:bg-destructive/90 rounded-sm btn-tactical"
              data-testid="submit-btn"
            >
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Add to Wishlist'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/wishlist')}
              className="rounded-sm"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
